import {
    Actor,
    Color,
    DefaultLoader,
    Engine,
    FadeInOut,
    Label,
    Scene,
    SceneActivationContext,
    vec
} from 'excalibur';
import { resources } from '../../../../Resources';
import { loadResources } from '../../../../shared/utilities/loadResources';
import { VietnamPro } from '../../../../shared/utilities/VietnamPro';

type SplashSpec = {
    image: { toSprite(): unknown };
    text: string;
};

type Phase = 'fadeIn' | 'hold' | 'fadeOut';

export class SplashScene extends Scene<unknown> {
    private readonly _splashes: SplashSpec[] = [
        { image: resources.games.helicopters.scenes.splash.bgPilots, text: 'mtcmndr and mredbishop present...' },
        { image: resources.games.helicopters.scenes.splash.bgSunset, text: 'A Shrapnel Studios Game' },
        { image: resources.games.helicopters.scenes.splash.bgShrapnel, text: 'Handle With Care' }
    ];

    private _engine?: Engine;

    private _imageActor?: Actor;
    private _label?: Label;

    private _index = 0;
    private _phase: Phase = 'fadeIn';
    private _phaseElapsedMs = 0;

    // [ED] Splash is currently timed to the track "Full Automatic" by Mark Turner :)
    private readonly _fadeMs = 250;
    private readonly _holdMs = 7250;

    private readonly _baseScale = 1.5;
    private readonly _zoomTo = 1.4;
    private _shotElapsedMs = 0;

    private readonly _textRisePx = 70;
    private _labelTargetY = 0;


    override onPreLoad(loader: DefaultLoader): void {
        loadResources(loader, resources.games.helicopters.scenes.splash, resources.games.helicopters.scenes.menu, resources.games.helicopters.actors.helicopter);
    }

    private goHomeEarly = () => this.goHome(this.engine);

    override onInitialize(engine: Engine): void {
        engine.input.keyboard.on('press', this.goHomeEarly);
        engine.input.pointers.on('down', this.goHomeEarly);

        this._imageActor = new Actor({ anchor: vec(0.5, 0.7) });

        this._label = new Label({
            text: '',
            pos: vec(engine.screen.width / 2, engine.screen.height)
        });

        this._label.font = VietnamPro;
        this._label.color = Color.White;

        this.add(this._imageActor);
        this.add(this._label);

        this.setAlpha(0);
    }

    override onActivate(context: SceneActivationContext<unknown, undefined>): void {
        const music = resources.games.helicopters.scenes.splash.musicFullAutomatic;
        music.loop = true;
        if (!music.isPlaying()) music.play(0.5);

        this._engine = context.engine;

        this._index = 0;
        this._phase = 'fadeIn';
        this._phaseElapsedMs = 0;
        this._shotElapsedMs = 0;

        this.applySplash(this._index);
        this.setAlpha(0);
    }

    override onDeactivate(_context: SceneActivationContext<undefined, undefined>): void {
        this._engine = undefined;
        this._phaseElapsedMs = 0;
        this._shotElapsedMs = 0;
        this._phase = 'fadeIn';
        this._index = 0;
        this.setAlpha(0);
    }

    override onPreUpdate(engine: Engine, elapsed: number): void {
        if (!this._engine || !this._imageActor || !this._label) return;

        this._phaseElapsedMs += elapsed;

        if (this._phase === 'fadeIn' || this._phase === 'hold' || this._phase === 'fadeOut') {
            this._shotElapsedMs += elapsed;
            this.applyCinematicZoom();
        }

        switch (this._phase) {
            case 'fadeIn': {
                const t = Math.min(1, this._phaseElapsedMs / this._fadeMs);
                this.setAlpha(t);
                if (t >= 1) {
                    this._phase = 'hold';
                    this._phaseElapsedMs = 0;
                }
                break;
            }

            case 'hold': {
                this.setAlpha(1);
                if (this._phaseElapsedMs >= this._holdMs) {
                    this._phase = 'fadeOut';
                    this._phaseElapsedMs = 0;
                }
                break;
            }

            case 'fadeOut': {
                const t = Math.min(1, this._phaseElapsedMs / this._fadeMs);
                this.setAlpha(1 - t);
                if (t >= 1) {
                    if (this._index >= this._splashes.length - 1) {
                        this.goHome(engine);
                        return;
                    }

                    this._index++;
                    this.applySplash(this._index);
                    this._phase = 'fadeIn';
                    this._phaseElapsedMs = 0;
                    this._shotElapsedMs = 0; // reset for next splash
                    this.setAlpha(0);
                }
                break;
            }
        }
    }

    private goHome(engine: Engine): void {
        this.engine.input.keyboard.off('press', this.goHomeEarly);
        this.engine.input.pointers.off('down', this.goHomeEarly);

        engine.goToScene('Menu', {
            sourceOut: new FadeInOut({
                duration: 350,
                direction: 'out',
                color: Color.Black
            }),
            destinationIn: new FadeInOut({
                duration: 350,
                direction: 'in',
                color: Color.Black
            })
        });
    }

    private applySplash(index: number): void {
        if (!this._engine || !this._imageActor || !this._label) return;

        const splash = this._splashes[index];

        const sprite = splash.image.toSprite() as Parameters<Actor['graphics']['use']>[0];
        this._imageActor.graphics.use(sprite);

        this._imageActor.pos = vec(this._engine.screen.width / 2, this._engine.screen.height / 2);
        this._imageActor.scale = vec(this._baseScale, this._baseScale);

        this._label.text = splash.text;

        this._labelTargetY = this._engine.screen.height * 0.68;
        this._label.pos = vec(this._engine.screen.width / 2, this._labelTargetY + this._textRisePx);
    }


    private applyCinematicZoom(): void {
        if (!this._engine || !this._imageActor || !this._label) return;
        const windowMs = this._fadeMs + this._holdMs;
        const t = Math.min(1, this._shotElapsedMs / windowMs);

        const easing = t * t * (3 - 2 * t);
        const scale = this._baseScale * (1 + (this._zoomTo - 1) * easing);
        this._imageActor.scale = vec(scale, scale);

        const zoomPullUp = (scale - this._baseScale) * 90;
        const targetY = this._labelTargetY - zoomPullUp;

        const y = targetY + (1 - easing) * this._textRisePx;
        this._label.pos = vec(this._engine.screen.width / 2, y);
    }


    private setAlpha(alpha: number): void {
        if (!this._imageActor || !this._label) return;
        this._imageActor.graphics.opacity = alpha;
        this._label.opacity = alpha;
    }
}
